import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Header from '../components/Header';
import LogService, { LOG_ACTIONS } from '../services/LogService';
import cubDataDefault, { TIPO_LABELS } from '../data/cubData';

function formatBRL(value) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function loadCubData() {
  try {
    const stored = localStorage.getItem('orcai_cub_data');
    if (stored) return JSON.parse(stored);
  } catch { /* fallback */ }
  return cubDataDefault;
}

function AdminCub() {
  const navigate = useNavigate();
  const [cubValues, setCubValues] = useState(() => loadCubData());
  const [filterEstado, setFilterEstado] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const logs = useMemo(() => LogService.getAll(), [refreshKey]);

  // KPIs
  const totalEstados = Object.keys(cubValues.estados).length;
  const cubCalculations = useMemo(
    () => logs.filter((l) => l.action === LOG_ACTIONS.CUB_CALCULATE).length,
    [logs]
  );
  const cubMedioBrasil = useMemo(() => {
    let soma = 0;
    let count = 0;
    Object.values(cubValues.estados).forEach((estado) => {
      Object.values(estado.tipos).forEach((tipo) => {
        soma += tipo.valor;
        count++;
      });
    });
    return count > 0 ? soma / count : 0;
  }, [cubValues]);

  // Flat rows for table
  const allRows = useMemo(() => {
    const rows = [];
    Object.entries(cubValues.estados).forEach(([sigla, estado]) => {
      Object.entries(estado.tipos).forEach(([codigo, tipo]) => {
        rows.push({
          sigla,
          estadoNome: estado.nome,
          codigo,
          tipoLabel: TIPO_LABELS[codigo] || tipo.descricao,
          descricao: tipo.descricao,
          valor: tipo.valor,
        });
      });
    });
    return rows;
  }, [cubValues]);

  const filteredRows = useMemo(() => {
    if (!filterEstado) return allRows;
    return allRows.filter((r) => r.sigla === filterEstado);
  }, [allRows, filterEstado]);

  const handleStartEdit = (sigla, codigo, currentValor) => {
    setEditingRow(`${sigla}-${codigo}`);
    setEditValue(currentValor.toString());
  };

  const handleSave = (sigla, codigo) => {
    const newValor = parseFloat(editValue);
    if (isNaN(newValor) || newValor <= 0) return;

    const oldValor = cubValues.estados[sigla].tipos[codigo].valor;
    const updated = JSON.parse(JSON.stringify(cubValues));
    updated.estados[sigla].tipos[codigo].valor = newValor;
    updated.data_atualizacao = new Date().toISOString().slice(0, 7);

    setCubValues(updated);
    localStorage.setItem('orcai_cub_data', JSON.stringify(updated));
    setEditingRow(null);

    LogService.log(LOG_ACTIONS.CUB_ADMIN_UPDATE, {
      details: { estado: sigla, tipo: codigo, valor_anterior: oldValor, valor_novo: newValor },
    });
    setRefreshKey((k) => k + 1);
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditValue('');
  };

  const handleRestoreDefaults = () => {
    if (window.confirm('Restaurar todos os valores CUB para o padrao? Esta acao sobrescreve edicoes manuais.')) {
      localStorage.removeItem('orcai_cub_data');
      setCubValues(cubDataDefault);
    }
  };

  const handleExportExcel = () => {
    const wsData = [
      ['Estado', 'Sigla', 'Codigo', 'Tipo', 'Descricao', 'Valor (R$/m2)'],
      ...allRows.map((r) => [r.estadoNome, r.sigla, r.codigo, r.tipoLabel, r.descricao, r.valor.toFixed(2)]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 20 }, { wch: 8 }, { wch: 10 }, { wch: 30 }, { wch: 40 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CUB');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `QuantoCustaObra_CUB_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Gerenciar CUB</h1>
            <p className="text-gray-500">Valores do Custo Unitario Basico por estado</p>
          </div>
          <div className="flex flex-wrap gap-2 mt-3 sm:mt-0">
            <button onClick={() => navigate('/admin/dashboard')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Dashboard</button>
            <button onClick={() => navigate('/admin/logs')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Logs</button>
            <button onClick={() => navigate('/admin/feedbacks')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Feedbacks</button>
            <button onClick={() => navigate('/admin/usuarios')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Usuarios</button>
            <button onClick={() => navigate('/admin/sinapi')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">SINAPI</button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Estados</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalEstados}</p>
            <p className="text-xs text-gray-400 mt-1">cadastrados</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Atualizado</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{cubValues.data_atualizacao}</p>
            <p className="text-xs text-gray-400 mt-1">ultima referencia</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">CUB Medio</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{formatBRL(cubMedioBrasil)}</p>
            <p className="text-xs text-gray-400 mt-1">media nacional</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Calculos</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{cubCalculations}</p>
            <p className="text-xs text-gray-400 mt-1">orcamentos CUB</p>
          </div>
        </div>

        {/* Filter + Actions */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                className="w-full sm:w-48 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
              >
                <option value="">Todos os estados</option>
                {Object.entries(cubValues.estados)
                  .sort(([, a], [, b]) => a.nome.localeCompare(b.nome))
                  .map(([sigla, data]) => (
                    <option key={sigla} value={sigla}>{data.nome} ({sigla})</option>
                  ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleRestoreDefaults}
                className="px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 cursor-pointer transition-colors"
              >
                Restaurar Padroes
              </button>
              <button
                onClick={handleExportExcel}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 cursor-pointer transition-colors"
              >
                Exportar Excel
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Estado</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Codigo</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Tipo</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider text-right">Valor (R$/m2)</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider text-center">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-400">Nenhum registro encontrado</td>
                  </tr>
                ) : (
                  filteredRows.map((row) => {
                    const rowKey = `${row.sigla}-${row.codigo}`;
                    const isEditing = editingRow === rowKey;
                    return (
                      <tr key={rowKey} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-700 font-medium">{row.estadoNome} ({row.sigla})</td>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-orca-100 text-orca-700">{row.codigo}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{row.descricao}</td>
                        <td className="px-4 py-3 text-right">
                          {isEditing ? (
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(row.sigla, row.codigo); if (e.key === 'Escape') handleCancelEdit(); }}
                              autoFocus
                              className="w-32 px-2 py-1 text-sm text-right rounded-lg border border-orca-300 bg-white focus:outline-none focus:ring-1 focus:ring-orca-500"
                            />
                          ) : (
                            <span className="font-medium text-gray-800">{formatBRL(row.valor)}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleSave(row.sigla, row.codigo)}
                                className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 cursor-pointer transition-colors"
                              >
                                Salvar
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 cursor-pointer transition-colors"
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleStartEdit(row.sigla, row.codigo, row.valor)}
                              className="px-2 py-1 text-xs font-medium text-orca-700 bg-orca-50 rounded hover:bg-orca-100 cursor-pointer transition-colors"
                            >
                              Editar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer info */}
        <div className="mt-4 text-xs text-gray-400 text-center">
          <p>Fonte: SINDUSCON | Referencia: {cubValues.data_atualizacao} | {allRows.length} registros em {totalEstados} estados</p>
        </div>
      </main>
    </div>
  );
}

export default AdminCub;
